import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { last12MonthSeries } from "../services/forecastService";
import { TREND_LABELS } from "../models/forecastResult";

function fmt(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function ForecastItemChart({ result, onClose }) {
  if (!result) return null;

  const history = last12MonthSeries(result.monthlySales);
  const forecastPoint = {
    label: "Next 15d",
    qty: result.forecast15Days,
    isForecast: true,
  };
  const combined = [
    ...history,
    { label: forecastPoint.label, qty: forecastPoint.qty, isForecast: true },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto p-5"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold">{result.itemName}</h3>
            <p className="text-sm text-gray-600">
              {TREND_LABELS[result.trend]} · {result.monthsOfHistory} months of history
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-red-600 font-bold text-lg px-2">
            ×
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-xs">
          <div className="bg-slate-50 p-2 rounded border">
            <div className="text-gray-500">Forecast 15 days</div>
            <div className="font-semibold">{fmt(result.forecast15Days)}</div>
          </div>
          <div className="bg-slate-50 p-2 rounded border">
            <div className="text-gray-500">Recommended</div>
            <div className="font-semibold">{fmt(result.recommendedQty)}</div>
          </div>
          <div className="bg-slate-50 p-2 rounded border">
            <div className="text-gray-500">Avg daily</div>
            <div className="font-semibold">{fmt(result.avgDailySales)}</div>
          </div>
          <div className="bg-slate-50 p-2 rounded border">
            <div className="text-gray-500">Growth %</div>
            <div className="font-semibold">
              {result.growthPercent != null ? `${result.growthPercent.toFixed(1)}%` : "—"}
            </div>
          </div>
        </div>

        <h4 className="text-sm font-semibold mb-2">Last 12 months sales</h4>
        <div className="h-56 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={history}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="qty" fill="#6366f1" name="Sales qty" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <h4 className="text-sm font-semibold mb-2">Sales trend + 15-day forecast</h4>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={combined}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="qty"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Qty"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
